<script setup lang="ts">
/**
 * MessageBubble — wrapper sobre uma mensagem individual (estilização por role).
 * Usado internamente por MessageList (exportado para reuso em outras telas).
 *
 * Fase 2 (Lume) B1: ReadAloud para mensagens do Companion (não do
 * learner — não lemos o que o usuário escreveu em voz alta).
 *
 * HLOS-319: renderiza anexos inline (imagem/sketch) em mensagens
 * do learner.
 */
import type { Message } from "../types";
import ReadAloud from "../../multimodal/components/ReadAloud.vue";

interface Props {
  message: Message;
}

defineProps<Props>();
</script>

<template>
  <div
    class="hlos-message-bubble"
    :data-role="message.role"
    :data-message-id="message.id"
  >
    <strong>{{ message.role === "learner" ? "Você" : "Companion" }}</strong>

    <!-- HLOS-319: anexos (imagem/sketch) inline. Renderiza ANTES do texto
         porque o anexo é o "assunto" da mensagem. -->
    <div
      v-if="message.attachments && message.attachments.length > 0"
      class="hlos-message-bubble__attachments"
      aria-label="Anexos"
    >
      <a
        v-for="(att, i) in message.attachments"
        :key="i"
        :href="att.dataUrl"
        :download="att.filename"
        :aria-label="`Anexo: ${att.filename ?? att.kind}`"
        class="hlos-message-bubble__attachment"
        :data-testid="`message-attachment-${att.kind}`"
      >
        <img
          :src="att.dataUrl"
          :alt="att.filename ?? 'anexo'"
          class="hlos-message-bubble__attachment-img"
        />
        <span v-if="att.filename" class="hlos-message-bubble__attachment-name">
          {{ att.filename }}
        </span>
      </a>
    </div>

    <p>{{ message.text }}</p>

    <!--
      HLOS-230: renderiza oferta de continuação (CC-01 — affordance visível).
      Aparece abaixo do texto principal quando Companion propôs caminhos
      concretos (R1 gentle offer, R2 provocation, ou fallback do bridge).
      Tom gentil, 2-3 opções, sem instrução. Testid usado por
      MessageBubble.spec.ts e cc-audit.spec.ts.
    -->
    <p
      v-if="message.offer"
      class="hlos-message-bubble__offer"
      data-testid="companion-offer"
      role="note"
      aria-label="Sugestões do Companheiro"
    >
      {{ message.offer }}
    </p>

    <!-- HLOS-319: ReadAloud em "variant=full" para ser mais descobrível.
         Aparece em TODAS as mensagens do Companion (era só ícone,
         usuários não achavam). Mostra label "Ouvir" / "Parar". -->
    <div
      v-if="message.role === 'companion'"
      class="hlos-message-bubble__actions"
    >
      <ReadAloud
        :text="message.text"
        variant="full"
        class="hlos-message-bubble__readaloud"
      />
    </div>
  </div>
</template>

<style scoped>
.hlos-message-bubble {
  padding: 0.5rem 0.75rem;
  margin: 0.25rem 0;
  border-radius: 0.5rem;
  background: color-mix(in srgb, currentColor 6%, transparent);
  display: inline-block;
  max-width: 85%;
}
.hlos-message-bubble[data-role="learner"] {
  margin-left: auto;
}
p {
  margin: 0.25rem 0 0 0;
}
/* HLOS-230: offer visualmente distinto do texto principal — affordance clara */
.hlos-message-bubble__offer {
  margin-top: 0.5rem;
  padding-top: 0.4rem;
  border-top: 1px dashed color-mix(in srgb, currentColor 25%, transparent);
  font-style: italic;
  opacity: 0.85;
}
.hlos-message-bubble__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  align-items: center;
}
.hlos-message-bubble__readaloud {
  display: inline-block;
}
/* HLOS-319: anexos inline */
.hlos-message-bubble__attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.hlos-message-bubble__attachment {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  text-decoration: none;
  color: inherit;
  border: 1px solid var(--hlos-color-neutral-200);
  border-radius: 6px;
  padding: 0.25rem;
  background: var(--hlos-color-bg);
  max-width: 180px;
}
.hlos-message-bubble__attachment:hover {
  background: var(--hlos-color-hover);
}
.hlos-message-bubble__attachment-img {
  display: block;
  max-width: 160px;
  max-height: 160px;
  border-radius: 4px;
  object-fit: cover;
}
.hlos-message-bubble__attachment-name {
  font-size: 0.75em;
  color: var(--hlos-color-neutral-600);
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
